import { OfflineBanner, ThemeToggle } from '../kit.js';
import { PanelTopBar } from '@saay/blocks';
import { formatWeekdayDate, todayBusinessDate } from '@saay/formatting';
import { Avatar, AvatarFallback } from '@saay/shadcn/components/avatar';
import { Button, buttonVariants } from '@saay/shadcn/components/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@saay/shadcn/components/sheet';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from '@saay/shadcn/components/sidebar';
import { cn } from '@saay/shadcn/lib/utils';
import { BrandPsi } from '@saay/ui';
import { ChevronLeftIcon, LogOutIcon, type LucideIcon, MenuIcon, PlusIcon } from 'lucide-react';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useLocation } from 'react-router';

import { ADMIN_ROUTES } from '../nav.js';

export interface ShellLink {
  /** The link's accessible name when it differs from the label (the approvals count). */
  readonly ariaLabel?: string;
  /** The approvals count badge. */
  readonly badge?: string;
  readonly current: boolean;
  readonly href: string;
  readonly icon: LucideIcon;
  readonly label: string;
  /** The label in the phone bottom bar. */
  readonly shortLabel?: string;
}

export interface ShellGroup {
  readonly items: readonly ShellLink[];
  readonly label?: string;
}

const ShellTitleContext = createContext<((title: string | null) => void) | null>(null);
const ShellSubtitleContext = createContext<((subtitle: string | null) => void) | null>(null);
const ShellCenterContext = createContext<((action: ShellCenterAction | null) => void) | null>(null);

/** The phone bar's raised centre action (the create link that used to be a corner FAB). */
export interface ShellCenterAction {
  readonly href: string;
  readonly label: string;
  readonly state?: unknown;
}

/** The slot inside `PanelTopBar` where a page portals its own controls (the dashboard refresh). */
export const ADMIN_TOPBAR_EXTRA_ID = 'admin-topbar-extra';

/** Registers the current page title with the shell bar. Outside the shell it does nothing. */
export function useRegisterPageTitle(title: string, active = true): void {
  const setTitle = useContext(ShellTitleContext);
  useEffect(() => {
    if (setTitle === null || !active) {
      return undefined;
    }
    setTitle(title);
    return () => setTitle(null);
  }, [active, setTitle, title]);
}

/**
 * Appends a page line to the bar's date subtitle (the dashboard «به‌روز …»). The date stays; the
 * page does not repeat the line under the title.
 */
export function useShellSubtitle(subtitle: string | null, active = true): void {
  const setSubtitle = useContext(ShellSubtitleContext);
  useLayoutEffect(() => {
    if (setSubtitle === null || !active) {
      return undefined;
    }
    setSubtitle(subtitle);
    return () => setSubtitle(null);
  }, [active, setSubtitle, subtitle]);
}

/**
 * Registers the compact-width create action as the phone bar's raised centre button.
 * Outside the shell, or when `action` is null, the bar has no centre button.
 */
export function useShellCenterAction(action: ShellCenterAction | null): void {
  const setCenter = useContext(ShellCenterContext);
  const href = action?.href ?? '';
  const label = action?.label ?? '';
  const state = action?.state;
  const active = action !== null;
  useEffect(() => {
    if (setCenter === null || !active) {
      return undefined;
    }
    setCenter({
      href,
      label,
      ...(state === undefined ? {} : { state }),
    });
    return () => setCenter(null);
  }, [active, href, label, setCenter, state]);
}

function sectionLabel(pathname: string): string | undefined {
  return ADMIN_ROUTES.filter(
    (route) => pathname === route.path || pathname.startsWith(`${route.path}/`),
  ).sort((left, right) => right.path.length - left.path.length)[0]?.labelFa;
}

function linkElement(item: ShellLink, onNavigate?: () => void) {
  return (
    <Link
      aria-current={item.current ? 'page' : undefined}
      {...(item.ariaLabel === undefined ? {} : { 'aria-label': item.ariaLabel })}
      {...(onNavigate === undefined ? {} : { onClick: onNavigate })}
      to={item.href}
    />
  );
}

/** Icon + label, with the active icon on a pale primary pill (BottomNav). */
function barItemClass(current: boolean): string {
  return cn(
    'relative h-full flex-1 flex-col gap-0.5 rounded-[24px] text-[11px] font-medium hover:bg-transparent [&_svg]:size-[22px]!',
    current ? 'text-primary hover:text-primary' : 'text-muted-foreground',
  );
}

function BarIcon({
  current,
  icon: Icon,
}: {
  readonly current: boolean;
  readonly icon: LucideIcon;
}) {
  return (
    <span
      className={cn('grid h-8 w-14 place-items-center rounded-full', current ? 'bg-primary/8' : '')}
    >
      <Icon aria-hidden="true" />
    </span>
  );
}

/**
 * The phone navigation below `md`: a floating glass bar (BottomNav's classes, full width) with the
 * role's destinations, then «بیشتر». A page's create action is the raised centre button.
 */
function PhoneNav({
  account,
  center,
  groups,
  items,
  onLogout,
}: {
  readonly account?: ShellLink;
  readonly center: ShellCenterAction | null;
  readonly groups: readonly ShellGroup[];
  readonly items: readonly ShellLink[];
  readonly onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const barItem = (item: ShellLink) => (
    <Button
      className={barItemClass(item.current)}
      key={item.href}
      nativeButton={false}
      render={linkElement(item)}
      variant="ghost"
    >
      <BarIcon current={item.current} icon={item.icon} />
      {item.badge === undefined ? null : (
        <span className="absolute top-1.5 start-1/2 ms-1.5 rounded-full bg-tone-danger-strong px-1.5 text-[10px] leading-4 text-white">
          {item.badge}
        </span>
      )}
      {item.shortLabel ?? item.label}
    </Button>
  );
  const more = (
    <Sheet key="more" onOpenChange={setOpen} open={open}>
      <Button
        className={barItemClass(false)}
        onClick={() => setOpen(true)}
        type="button"
        variant="ghost"
      >
        <BarIcon current={false} icon={MenuIcon} />
        بیشتر
      </Button>
      <SheetContent className="max-h-[85dvh] gap-2 overflow-y-auto rounded-t-3xl" side="bottom">
        <SheetHeader>
          <SheetTitle>بیشتر</SheetTitle>
          <SheetDescription className="sr-only">همهٔ بخش‌های پنل مدیریت</SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-4 px-4 pb-6">
          {groups.map((group, index) => (
            <div className="flex flex-col gap-1" key={group.label ?? index}>
              {group.label === undefined ? null : (
                <p className="px-3 text-xs text-muted-foreground">{group.label}</p>
              )}
              {group.items.map((item) => (
                <Button
                  className="h-11 justify-start gap-3 rounded-xl px-3 data-[current=true]:bg-sidebar-accent data-[current=true]:font-bold data-[current=true]:text-sidebar-accent-foreground"
                  data-current={item.current}
                  key={item.href}
                  nativeButton={false}
                  render={linkElement(item, close)}
                  variant="ghost"
                >
                  <item.icon aria-hidden="true" />
                  <span className="flex-1 text-start">{item.label}</span>
                  {item.badge === undefined ? null : (
                    <span className="rounded-full bg-tone-danger-strong px-1.5 text-[11px] text-white">
                      {item.badge}
                    </span>
                  )}
                </Button>
              ))}
            </div>
          ))}
          <div className="flex flex-col gap-1">
            {account === undefined ? null : (
              <Button
                className="h-11 justify-start gap-3 rounded-xl px-3"
                nativeButton={false}
                render={linkElement(account, close)}
                variant="ghost"
              >
                <account.icon aria-hidden="true" />
                {account.label}
              </Button>
            )}
            <Button
              className="h-11 justify-start gap-3 rounded-xl px-3"
              onClick={onLogout}
              type="button"
              variant="ghost"
            >
              <LogOutIcon aria-hidden="true" />
              خروج
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
  const entries = [...items.map((item) => barItem(item)), more];
  const half = Math.ceil(entries.length / 2);
  return (
    <nav
      aria-label="منوی پایین"
      className="fixed inset-x-0 bottom-0 z-30 px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]"
    >
      <div className="glass flex h-[60px] items-stretch rounded-[24px]">
        {center === null ? (
          entries
        ) : (
          <>
            {entries.slice(0, half)}
            <div className="flex flex-1 justify-center">
              <Link
                aria-label={center.label}
                className={buttonVariants({
                  className:
                    '-mt-4 size-14 rounded-full shadow-[0_6px_14px_-8px_var(--glass-shadow)] ring-4 ring-background [&_svg]:size-6!',
                  size: 'icon',
                })}
                to={center.href}
                {...(center.state === undefined ? {} : { state: center.state })}
              >
                <PlusIcon aria-hidden="true" />
              </Link>
            </div>
            {entries.slice(half)}
          </>
        )}
      </div>
    </nav>
  );
}

/**
 * The admin shell (D-93): the panel frame with the grouped admin navigation in the Sidebar from
 * `md` up and the phone bar below it, and `PanelTopBar` with the page title, search, theme, and
 * logout. Page titles are registered by `usePageTitle`.
 */
export function AdminShell({
  account,
  children,
  groups,
  headerSearch,
  onLogout,
  phoneItems,
  user,
}: {
  readonly account?: ShellLink;
  readonly children: ReactNode;
  readonly groups: readonly ShellGroup[];
  readonly headerSearch?: ReactNode;
  readonly onLogout: () => void;
  readonly phoneItems: readonly ShellLink[];
  readonly user: { readonly name: string; readonly roleLabel: string };
}) {
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const [subtitleExtra, setSubtitleExtra] = useState<string | null>(null);
  const [center, setCenter] = useState<ShellCenterAction | null>(null);
  const { pathname } = useLocation();
  const section = useMemo(() => sectionLabel(pathname), [pathname]);
  const title = pageTitle ?? section ?? 'SAAY';
  const frame = useRef<HTMLElement>(null);

  // The page keeps its own h1 for assistive technology; the bar shows the title once (owner W4).
  useEffect(() => {
    const root = document.getElementById('main');
    if (root === null) {
      return undefined;
    }
    const mark = () => {
      for (const heading of root.querySelectorAll('.saay-page-header h1')) {
        heading.classList.add('saay-visually-hidden');
      }
    };
    mark();
    const observer = new MutationObserver(mark);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // PanelTopBar's title repeats the page h1, which stays the heading for assistive technology.
  useLayoutEffect(() => {
    frame.current?.querySelector(':scope > header h1')?.setAttribute('aria-hidden', 'true');
  });

  // Sticky page titles sit under the floating bar (its height includes the edge gap).
  useEffect(() => {
    const root = frame.current;
    const header = root?.querySelector<HTMLElement>(':scope > header') ?? null;
    if (root === null || header === null || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const update = () => {
      const end = `${header.offsetHeight}px`;
      root.style.setProperty('--saay-shell-header-end', end);
      document.documentElement.style.setProperty('--saay-shell-header-end', end);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(header);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      root.style.removeProperty('--saay-shell-header-end');
      document.documentElement.style.removeProperty('--saay-shell-header-end');
    };
  }, []);

  const dateLine = formatWeekdayDate(todayBusinessDate(), { withYear: true });
  const subtitle = subtitleExtra === null ? dateLine : `${dateLine}، ${subtitleExtra}`;

  return (
    <ShellTitleContext.Provider value={setPageTitle}>
      <ShellSubtitleContext.Provider value={setSubtitleExtra}>
        <ShellCenterContext.Provider value={setCenter}>
          {/* The PanelShell frame (D-93) with one main landmark: the block nests a second <main>. */}
          <SidebarProvider className="admin-shell min-h-dvh">
            <Sidebar
              className="sticky top-0 hidden h-dvh w-64 border-e md:flex"
              collapsible="none"
              side="right"
            >
              <SidebarHeader className="p-4">
                <div className="flex items-center gap-3 rounded-2xl bg-muted p-2.5">
                  <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand p-2 text-brand-gold">
                    <BrandPsi className="size-full" />
                  </span>
                  <div className="min-w-0 flex-1 leading-tight">
                    <p className="truncate text-sm font-bold" translate="no">
                      SAAY
                    </p>
                    <p className="truncate text-xs text-muted-foreground">پنل مدیریت پلتفرم</p>
                  </div>
                </div>
              </SidebarHeader>
              <SidebarContent aria-label="منوی اصلی" className="px-2" role="navigation">
                {groups.map((group, index) => (
                  <SidebarGroup className="py-0.5" key={group.label ?? index}>
                    {group.label === undefined ? null : (
                      <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
                    )}
                    <SidebarGroupContent>
                      <SidebarMenu className="gap-0.5">
                        {group.items.map((item) => (
                          <SidebarMenuItem key={item.href}>
                            <SidebarMenuButton
                              className="h-9 rounded-xl px-3 text-muted-foreground hover:text-foreground data-active:font-bold [&_svg]:size-[18px]"
                              isActive={item.current}
                              render={linkElement(item)}
                            >
                              <item.icon aria-hidden="true" />
                              <span>{item.label}</span>
                            </SidebarMenuButton>
                            {item.badge === undefined ? null : (
                              <SidebarMenuBadge className="top-2! end-2 rounded-full bg-tone-danger-strong px-1.5 text-[11px] text-white peer-data-active/menu-button:text-white">
                                {item.badge}
                              </SidebarMenuBadge>
                            )}
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </SidebarGroup>
                ))}
              </SidebarContent>
              <SidebarFooter className="p-4">
                <SidebarMenuButton
                  aria-label="حساب من"
                  className="h-auto gap-3 rounded-2xl p-2"
                  isActive={account?.current === true}
                  {...(account === undefined ? {} : { render: linkElement(account) })}
                >
                  <Avatar className="size-9">
                    <AvatarFallback className="bg-tone-gold text-sm font-bold text-tone-gold-foreground">
                      {user.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 leading-tight">
                    <p className="truncate text-sm font-bold">{user.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{user.roleLabel}</p>
                  </div>
                  <ChevronLeftIcon
                    aria-hidden="true"
                    className="ms-auto size-4 text-muted-foreground"
                  />
                </SidebarMenuButton>
              </SidebarFooter>
            </Sidebar>
            <SidebarInset className="min-w-0 bg-background" id="main" ref={frame} tabIndex={-1}>
              <a
                className="sr-only focus:not-sr-only focus:absolute focus:start-4 focus:top-2 focus:z-30"
                href="#main"
              >
                رفتن به محتوای اصلی
              </a>
              <PanelTopBar subtitle={subtitle} title={title}>
                {headerSearch}
                <div className="contents" id={ADMIN_TOPBAR_EXTRA_ID} />
                <ThemeToggle className="rounded-full bg-muted" />
                <Button
                  aria-label="خروج"
                  className="hidden rounded-full bg-muted md:inline-flex"
                  onClick={onLogout}
                  size="icon"
                  title="خروج"
                  type="button"
                  variant="ghost"
                >
                  <LogOutIcon aria-hidden="true" />
                </Button>
              </PanelTopBar>
              <div className="flex min-w-0 flex-col gap-4 px-4 pt-3 pb-[calc(7rem+env(safe-area-inset-bottom))] md:gap-6 md:px-8 md:pb-10">
                <OfflineBanner />
                {children}
              </div>
              <div className="md:hidden">
                <PhoneNav
                  {...(account === undefined ? {} : { account })}
                  center={center}
                  groups={groups}
                  items={phoneItems}
                  onLogout={onLogout}
                />
              </div>
            </SidebarInset>
          </SidebarProvider>
        </ShellCenterContext.Provider>
      </ShellSubtitleContext.Provider>
    </ShellTitleContext.Provider>
  );
}
